import { useState, useEffect } from 'react';
import { jobAPI } from '../../services/api';
import { Link } from 'react-router-dom';
import { FaBriefcase, FaMapMarkerAlt, FaDollarSign, FaUsers, FaArrowRight, FaPlus } from 'react-icons/fa';
import {
    DashboardHeader,
    DashboardFilters,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const JobBoard = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        job_type: '',
        location: ''
    });

    useEffect(() => {
        loadJobs();
    }, [filters]);

    const loadJobs = async () => {
        setLoading(true);
        try {
            const response = await jobAPI.getAll(filters);
            setJobs(response.jobs || []);
        } catch (err) {
            console.error('Failed to load jobs:', err);
        } finally {
            setLoading(false);
        }
    };

    const filterConfig = [
        {
            key: 'job_type',
            options: [
                { value: '', label: 'All Types' },
                { value: 'full_time', label: 'Full Time' },
                { value: 'part_time', label: 'Part Time' },
                { value: 'internship', label: 'Internship' },
                { value: 'contract', label: 'Contract' },
                { value: 'freelance', label: 'Freelance' }
            ]
        },
        {
            key: 'location',
            options: [
                { value: '', label: 'All Locations' }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                    <DashboardHeader
                        icon={FaBriefcase}
                        title="Job Board"
                        subtitle="Find opportunities from our partner employers"
                    />
                    <Link
                        to="/jobs/post"
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md"
                    >
                        <FaPlus /> Post a Job
                    </Link>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                            <select
                                value={filters.job_type}
                                onChange={(e) => setFilters({...filters, job_type: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">All Types</option>
                                <option value="full_time">Full Time</option>
                                <option value="part_time">Part Time</option>
                                <option value="internship">Internship</option>
                                <option value="contract">Contract</option>
                                <option value="freelance">Freelance</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                            <input
                                type="text"
                                value={filters.location}
                                onChange={(e) => setFilters({...filters, location: e.target.value})}
                                placeholder="Search by location"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : jobs.length === 0 ? (
                    <EmptyState
                        icon={FaBriefcase}
                        message="No jobs available at the moment."
                    />
                ) : (
                    <div className="space-y-4">
                        {jobs.map((job) => (
                            <Link
                                key={job.id}
                                to={`/jobs/${job.id}`}
                                className="block bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
                                        <p className="text-gray-600 mb-2 font-medium">{job.company_name}</p>
                                        <p className="text-gray-700 mb-4 line-clamp-2">{job.description?.substring(0, 200)}...</p>
                                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                            {job.location && (
                                                <div className="flex items-center gap-1">
                                                    <FaMapMarkerAlt className="text-gray-400" />
                                                    <span>{job.location}</span>
                                                </div>
                                            )}
                                            {job.job_type && (
                                                <div className="flex items-center gap-1">
                                                    <FaBriefcase className="text-gray-400" />
                                                    <span>{job.job_type.replace('_', ' ')}</span>
                                                </div>
                                            )}
                                            {job.salary_range && (
                                                <div className="flex items-center gap-1">
                                                    <FaDollarSign className="text-gray-400" />
                                                    <span>{job.salary_range}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1">
                                                <FaUsers className="text-gray-400" />
                                                <span>{job.applications_count || 0} applications</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ml-4 flex items-center">
                                        <span className="text-blue-600 font-medium">
                                            View Details <FaArrowRight className="inline ml-1" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobBoard;
